import { useForm } from "@tanstack/react-form";
import { Building2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadScreen } from "@/core/components/shared";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCompanyProfile } from "@/features/kyc/api/use-company-profile";
import { useCreateCompanyProfile } from "@/features/kyc/api/use-create-company-profile";
import type { CompanyProfileDto } from "@/features/kyc/api/dto";
import {
  createCompanyProfileSchema,
  type CompanyProfileFormValues,
} from "@/features/kyc/schemas/company-profile.schema";
import type { PartyType } from "@/features/shared/api/enums";
import { usePartySelectOptions } from "@/features/shared/api";

const EMPTY_VALUES: CompanyProfileFormValues = {
  companyName: "",
  registrationNumber: "",
  taxId: "",
  legalRepresentativePartyId: "",
  incorporationDate: "",
  sectorActivity: "",
};

/** Chaînes vides → undefined : les champs omis restent absents du corps envoyé. */
const toPayload = (values: CompanyProfileFormValues): Partial<CompanyProfileDto> =>
  Object.fromEntries(
    Object.entries(values).map(([k, v]) => [k, v === "" ? undefined : v])
  ) as unknown as Partial<CompanyProfileDto>;

/**
 * Profil entreprise d'une identité COMPANY, affiché sur la page détail Party :
 * - profil existant → champs en lecture (NoTranslate) ;
 * - 404 / absent → bouton ouvrant le formulaire de création
 *   (`POST /CompanyProfiles/add`, partyId pré-rempli non éditable).
 * Renvoie `null` pour toute identité qui n'est pas de type COMPANY.
 */
export function CompanyProfileCard({
  partyId,
  partyType,
}: {
  partyId: string;
  partyType: PartyType;
}) {
  const { t } = useTypedTranslation();
  // Clés kyc.companyProfile.* fusionnées dans les locales — tk (non typé)
  // reste utilisé pour ne pas multiplier les casts historiques.
  const { t: tk } = useTranslation();
  const isCompany = partyType === "COMPANY";
  const { data: profile, isLoading, error, refetch } = useCompanyProfile(
    partyId,
    isCompany
  );
  const [createOpen, setCreateOpen] = useState(false);

  if (!isCompany) return null;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex h-40 items-center justify-center">
          <LoadScreen />
        </CardContent>
      </Card>
    );
  }

  const isNotFound = Boolean(error) && /404/.test(error?.message ?? "");

  if (error && !isNotFound) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{tk("kyc.companyProfile.title")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-start gap-3">
          <p className="text-sm text-destructive">{error.message}</p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t("common.retry")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!profile) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{tk("kyc.companyProfile.title")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-start gap-3">
          <p className="text-sm text-muted-foreground">
            {tk("kyc.companyProfile.empty")}
          </p>
          <Button variant="outline" onClick={() => setCreateOpen(true)}>
            <Building2 className="mr-2 h-4 w-4" />
            {tk("kyc.companyProfile.create")}
          </Button>
        </CardContent>
        <CompanyProfileFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          partyId={partyId}
        />
      </Card>
    );
  }

  const fields: { label: string; value?: string }[] = [
    { label: tk("kyc.companyProfile.form.companyName"), value: profile.companyName },
    {
      label: tk("kyc.companyProfile.form.registrationNumber"),
      value: profile.registrationNumber,
    },
    { label: tk("kyc.companyProfile.form.taxId"), value: profile.taxId },
    {
      label: tk("kyc.companyProfile.form.legalRepresentativePartyId"),
      value: profile.legalRepresentativePartyId,
    },
    {
      label: tk("kyc.companyProfile.form.incorporationDate"),
      value: profile.incorporationDate,
    },
    { label: tk("kyc.companyProfile.form.sectorActivity"), value: profile.sectorActivity },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{tk("kyc.companyProfile.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {fields.map(({ label, value }) => (
            <div key={label}>
              <dt className="text-sm text-muted-foreground">{label}</dt>
              <dd className="font-medium">
                <NoTranslate as="span">{value || "—"}</NoTranslate>
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

/**
 * Formulaire de création du profil entreprise — TanStack Form + schéma zod
 * mémoïsé sur la langue courante. `partyId` est injecté au submit (affiché
 * en lecture seule).
 */
function CompanyProfileFormDialog({
  open,
  onOpenChange,
  partyId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partyId: string;
}) {
  const { t } = useTypedTranslation();
  // Clés kyc.companyProfile.* en attente de fusion dans les locales.
  const { t: tk, i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createCompanyProfileSchema(), [i18n.language]);

  const createCompanyProfile = useCreateCompanyProfile();
  const pending = createCompanyProfile.isPending;
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);

  // Radix démonte le contenu du Dialog à la fermeture : à chaque ouverture,
  // le formulaire repart des valeurs vides.
  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const payload: CompanyProfileDto = {
        ...toPayload(parsed.data),
        partyId,
        companyName: parsed.data.companyName,
      };
      createCompanyProfile.mutate(payload, {
        onSuccess: () => {
          notifSilence(tk("kyc.companyProfile.feedback.created"));
          onOpenChange(false);
        },
        onError: (mutationError) => notifSilenceError(mutationError.message),
      });
    },
  });

  const fieldError =
    (name: keyof CompanyProfileFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{tk("kyc.companyProfile.create")}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="partyId">{tk("kyc.companyProfile.form.partyId")}</Label>
            <NoTranslate as="span">
              <Input id="partyId" value={partyId} disabled readOnly />
            </NoTranslate>
          </div>

          {(
            [
              ["companyName", "text"],
              ["registrationNumber", "text"],
              ["taxId", "text"],
              ["incorporationDate", "date"],
              ["sectorActivity", "text"],
            ] as const
          ).map(([name, type]) => (
            <form.Field key={name} name={name} validators={{ onChange: fieldError(name) }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor={name}>{tk(`kyc.companyProfile.form.${name}`)}</Label>
                  <Input
                    id={name}
                    type={type}
                    value={(field.state.value as string) ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value as never)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          ))}

          <form.Field
            name="legalRepresentativePartyId"
            validators={{ onChange: fieldError("legalRepresentativePartyId") }}
          >
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="legalRepresentativePartyId">
                  {tk("kyc.companyProfile.form.legalRepresentativePartyId")}
                </Label>
                <EntitySearchableSelect
                  id="legalRepresentativePartyId"
                  options={partyOptions}
                  value={field.state.value ?? ""}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder={tk(
                    "kyc.companyProfile.form.legalRepresentativePartyIdPlaceholder"
                  )}
                  isLoading={partiesLoading}
                  clearable
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
